import React, { useState } from 'react'
import {
  COLUMN_MIN_WIDTH,
  ROW_NUMBER_COLUMN_WIDTH,
  ROW_NUMBER_COLUMN_WIDTH_CLASS,
  SMALL_ROW_HEIGHT_CLASS
} from 'style/constants'
import RowNumberCell from './RowNumberCell'
import { Plus } from 'components/icons'
import Tooltip from 'components/tooltip'
import { IColumnValuesCount, IViewPortColumn, rowHeights } from 'components/spreadsheet/types'
import { ITableRow, ICellValue, IContextMenuState, ITableViewColour } from 'types'
import { columnStoreAt, getRowValue, isRowInGroup } from 'components/spreadsheet/helpers/functions'
import { getCellColour, getRowLevelColour } from 'components/spreadsheet/helpers/colouring'
import { useApplicationStore } from 'hooks/application'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { useThemedColours } from 'hooks/useTheme'
import { addRowGroupCellStyles } from 'components/spreadsheet/components/addRow/styles'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'

interface RowProps {
  onClick: (values?: Record<string, ICellValue>, nextRowPublicId?: string) => Promise<void>
  rowNumber: number
  uniqueNumber: number
  columns: IViewPortColumn[]
  colourSettings?: ITableViewColour[]
  lastGroupRow?: ITableRow
  columnValuesCount: IColumnValuesCount
  addRowOffset?: number
  onGroupContext?: (menu: IContextMenuState) => void
}

const AddRow: React.FC<RowProps> = ({
  onClick,
  rowNumber,
  uniqueNumber,
  lastGroupRow,
  columns,
  colourSettings,
  columnValuesCount,
  addRowOffset = 0,
  onGroupContext
}) => {
  const themed = useThemedColours()
  const { displayErrorMessage } = useApplicationStore()
  const { spreadsheetData, selectedColumnIds, selectGroupCell } = useDataContext()
  // Through columnStoreAt: edits change the store in place, so the last row's values are read again when they do.
  const columnStore = columnStoreAt(spreadsheetData.columnStore, spreadsheetData.dataVersion)
  // Where along the bar "+ New row" points, from its left edge: under the pointer, or at the + when
  // it has keyboard focus. Null while it isn't shown. (Attached to the whole bar, the tooltip would
  // centre itself far along a wide table.) `moves` counts the pointer's moves while it shows; see
  // the anchor below. `pressed`: put away by a press until the pointer leaves and comes back.
  const [hint, setHint] = useState<{ x: number | null; moves: number; pressed: boolean }>({
    x: null,
    moves: 0,
    pressed: false
  })
  const showHintAt = (x: number | null, pressed = false): void => setHint(({ moves }) => ({ x, moves, pressed }))

  const columnLookup = new Map(spreadsheetData.viewDetails.columns.map((col) => [col.publicId, col]))
  const hasRowLevelRules = colourSettings?.some((setting) => setting.applyToEntireRow) ?? false
  const rowLevelColour =
    hasRowLevelRules && lastGroupRow
      ? getRowLevelColour(
          colourSettings!,
          lastGroupRow,
          columnLookup,
          spreadsheetData.columnIdToIndex,
          columnValuesCount,
          true,
          undefined,
          columnStore
        )
      : null

  const groupedCells = columns.filter((c) => c.isGrouped)
  const groupedPublicIds = groupedCells.map((c) => c.column.publicId)

  // Used to check if we're anywhere inside the group cell (including in the +row row)
  const groupSettingsIndexMap = new Map(
    spreadsheetData.userConfiguration.groupSettings.map((group, index) => [group.columnId, index])
  )
  const groupKeyFor = (columnId: string): string | undefined => {
    if (!lastGroupRow || !isRowInGroup(lastGroupRow)) return undefined
    const keyIndex = groupSettingsIndexMap.get(columnId)
    return keyIndex === undefined ? undefined : lastGroupRow.groupKeys[keyIndex]
  }

  // The "+ Row" button sits at the first non-grouped column but must stay pinned to the left of the
  // grid as the user scrolls horizontally (issue #10461). Pin its cell sticky at the right edge of
  // the frozen region (the row-number column when nothing is frozen), so it stays reachable on wide
  // tables. When the button's own column is frozen its existing left offset is already correct.
  const addRowColumn = columns[groupedCells.length]
  const frozenColumns = columns.filter((c) => c.isFrozen)
  const frozenBoundary =
    frozenColumns.length > 0 ? Math.max(...frozenColumns.map((c) => c.left + c.column.width)) : ROW_NUMBER_COLUMN_WIDTH
  const addRowStickyLeft = addRowColumn?.isFrozen ? addRowColumn.left : frozenBoundary

  // In tree view mode, grouped columns are removed from the viewport columns prop.
  // Fall back to groupSettings + viewDetails.columns to resolve grouped column IDs.
  const allGroupedPublicIds =
    groupedPublicIds.length > 0
      ? groupedPublicIds
      : spreadsheetData.userConfiguration.groupSettings.map((g) => g.columnId)

  const handleClick = () => {
    if (!lastGroupRow) {
      onClick()
      return
    }

    const values: Record<string, ICellValue> = {}
    for (const id of allGroupedPublicIds) {
      const viewColumn = columns.find((c) => c.column.publicId === id)
      const rawColumn = viewColumn?.column ?? spreadsheetData.viewDetails.columns.find((c) => c.publicId === id)
      if (!rawColumn) {
        displayErrorMessage(new Error(`Could not find column with public ID ${id}`))
        return
      }

      if (!rawColumn.formulaEnabled && !rawColumn.scriptEnabled && !rawColumn.isJoined) {
        values[id] = getRowValue(lastGroupRow, id, spreadsheetData.columnIdToIndex, columnStore)
      }
    }
    onClick(values)
  }

  // The whole row adds a row, except its group cells, which select their group (as they do on any
  // other row), and the new-column lane at its end, which isn't part of the table.
  const isOffBar = (target: EventTarget): boolean =>
    !!(target as HTMLElement).closest('[data-testid="add-row-group-cell"], [data-testid="add-row-new-column-lane"]')
  const handleRowClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (isOffBar(event.target)) return
    handleClick()
  }
  const handleRowKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    // Handled here, so the grid's own key handling (Ctrl+Enter adds a row from the last cell)
    // doesn't add a second one.
    event.stopPropagation()
    handleClick()
  }

  const pointerX = (event: React.MouseEvent<HTMLDivElement>): number =>
    Math.round(event.clientX - event.currentTarget.getBoundingClientRect().left)
  const plusX = (bar: HTMLElement): number => {
    const plus = bar.querySelector('[data-testid="add-row-button"]')?.getBoundingClientRect()
    return plus ? Math.round(plus.left + plus.width / 2 - bar.getBoundingClientRect().left) : 0
  }

  return (
    // A quiet row rather than a button: white like the rows above, with a
    // muted + where the row number goes. On hover (or keyboard focus) the whole
    // row, + included, takes the rows' hover tint, the + darkens and a "+ New row"
    // tooltip follows the pointer along it, so it reads as one bar you can click
    // anywhere on.
    <div
      key="add-row"
      role="button"
      tabIndex={0}
      aria-label="New row"
      onClick={handleRowClick}
      onKeyDown={handleRowKeyDown}
      onMouseEnter={(event) => showHintAt(isOffBar(event.target) ? null : pointerX(event))}
      onMouseMove={(event) => {
        const x = isOffBar(event.target) ? null : pointerX(event)
        setHint((current) => {
          if (current.pressed || current.x === x) return current
          // Appearing or going: the same anchor. Moving along: a new one.
          if (current.x === null || x === null) return { ...current, x }
          return { ...current, x, moves: current.moves + 1 }
        })
      }}
      onMouseLeave={() => showHintAt(null)}
      // Put away on press, as any tooltip is, until the pointer comes back: the new row moves the
      // bar out from under it.
      onMouseDown={() => showHintAt(null, true)}
      onFocus={(event) => showHintAt(event.currentTarget.matches(':focus-visible') ? plusX(event.currentTarget) : null)}
      onBlur={() => showHintAt(null)}
      id={
        lastGroupRow === undefined
          ? `add-row-${uniqueNumber}`
          : `add-group-row-${uniqueNumber}-${lastGroupRow.publicId}`
      }
      className="group absolute top-0 inline-flex items-start bg-surface hover:bg-surface-muted m-0 p-0 z-0 cursor-pointer outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue"
      style={{
        width: 'var(--grid-width)',
        transform: `translateY(calc(var(--header-height) + ${rowNumber} * var(--row-height) + var(--row-top-offset) - ${addRowOffset}px))`
      }}
      data-testid="add-row"
      data-tour="add-row"
    >
      {/* Beside a group's cells it closes the row numbers' column with their line; with nothing grouped, the
          bar runs on unbroken. */}
      <div
        data-testid="add-row-button"
        className={`sticky left-0 z-[4] flex items-center justify-center bg-inherit text-muted group-hover:text-body group-focus-visible:text-body transition-colors [border-top:1px_solid_var(--color-divider)] [&>svg]:w-3 [&>svg]:h-3 ${groupedCells.length > 0 ? '[border-right:1px_solid_var(--color-divider)]' : ''} ${SMALL_ROW_HEIGHT_CLASS} ${ROW_NUMBER_COLUMN_WIDTH_CLASS}`}
      >
        <Plus aria-hidden strokeWidth={1.5} />
      </div>
      <Tooltip content="+ New row" visible={hint.x !== null}>
        {/* What the tooltip points at: a sliver of the bar, moved to the pointer. A new one each
            move, so the tooltip re-anchors to it; but the same one as it appears, so it isn't
            measured from one that's just been removed (which puts it in the window's corner). */}
        <span
          key={hint.moves}
          data-testid="add-row-hint-anchor"
          aria-hidden
          className="absolute top-0 h-full w-0 pointer-events-none"
          style={{ left: hint.x ?? 0 }}
        />
      </Tooltip>

      {groupedCells.map((c) => {
        const cellValue = lastGroupRow
          ? getRowValue(lastGroupRow, c.column.publicId, spreadsheetData.columnIdToIndex, columnStore)
          : undefined
        const cellColour = lastGroupRow
          ? getCellColour(
              c.column,
              columnValuesCount,
              lastGroupRow,
              cellValue,
              colourSettings,
              true,
              undefined,
              rowLevelColour
            )
          : null
        const groupKey = groupKeyFor(c.column.publicId)
        const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>): void => {
          if (event.button !== 0 || !groupKey || !lastGroupRow) return
          selectGroupCell({ columnId: c.column.publicId, groupKey, rowId: lastGroupRow.publicId })
        }

        const handleContextMenu = (event: React.MouseEvent<HTMLDivElement>): void => {
          if (!onGroupContext || !groupKey) return
          event.preventDefault()
          onGroupContext({
            ...INITIAL_CONTEXT_MENU_STATE,
            open: true,
            top: `${event.clientY + 5}px`,
            left: `${event.clientX - 120}px`,
            right: 'auto',
            bottom: 'auto',
            columnId: c.column.publicId,
            groupKey,
            rowId: lastGroupRow?.publicId
          })
        }

        return (
          <div
            key={`grouped-column-border-${c.column.publicId}`}
            data-testid="add-row-group-cell"
            data-columnid={c.column.publicId}
            onMouseDown={handleMouseDown}
            onContextMenu={handleContextMenu}
            className={addRowGroupCellStyles({
              frozen: c.isFrozen,
              lastFrozen: c.isLastFrozenColumn,
              inSelectedColumn: selectedColumnIds.includes(c.column.publicId)
            })}
            style={{
              width: `${c.column.width}px`,
              backgroundColor: themed.fill(cellColour?.backgroundColour) || 'var(--color-surface-sunken)',
              ...(c.isFrozen ? { left: `${c.left}px` } : {})
            }}
          />
        )
      })}

      {groupedCells.length < columns.length &&
        !(spreadsheetData.viewDetails.treeGroupView && spreadsheetData.userConfiguration.groupSettings.length > 0) && (
          <RowNumberCell
            loading={false}
            rowHeight={rowHeights.Small}
            left={addRowStickyLeft}
            width={columns[groupedCells.length].column.width}
            isLastFrozen={columns[groupedCells.length].isLastFrozenColumn}
          />
        )}

      {/* The rest of the row, so its line runs all the way along to the
          new-column lane, and its right line carries on the table's right edge.
          Like every row, it draws its line along its top edge; the row below
          draws the next one, so the line between groups is always single.
          It turns white on hover with the row. */}
      <div
        data-testid="add-row-rest"
        className={`flex-1 box-border bg-inherit [border-top:1px_solid_var(--color-divider)] [border-right:1px_solid_var(--color-divider)] ${SMALL_ROW_HEIGHT_CLASS}`}
      />

      {spreadsheetData.isAdmin && (
        <div
          data-testid="add-row-new-column-lane"
          className={`bg-surface-sunken cursor-default ${SMALL_ROW_HEIGHT_CLASS}`}
          style={{ width: `${COLUMN_MIN_WIDTH}px` }}
        />
      )}
    </div>
  )
}

export default AddRow
